import axios, { AxiosError, AxiosResponse } from "axios";
import React, { useEffect, useState } from "react";
import { catchAsync, cn, IS_SUCCESS_RESPONSE } from "src/helpers";
import { useAuth } from "src/modules/Auth/Core";
import { axiosInstance } from "src/services/axiosSetup";
import { Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/shadcn/components/ui";
import PhoneNumberVerification from "./PhoneNumberVerification";
import { DownloadCloud, Loader2Icon } from "lucide-react";
import { useMainContext } from "src/pages/MYTReportCopy/Components/MainContext";
import { toast } from "src/shadcn/components/ui/use-toast";

const headerData = ["Project Parameters", "Units", "CPP", "GCP", "TPOA"];

type Props = {
  formData: any;
};

const Projectsuggestion = ({ formData }: Props) => {

  const [data, setData] = useState<any[]>([]);
  const [isLoading, setisLoading] = useState<boolean>(false);
  const [downloading, setdownloading] = useState<boolean>(false);
  const [reportUrl, setreportUrl] = useState<string | undefined>(undefined)
  const { currentUser } = useAuth()

  const handleProjectSuggestionData = catchAsync(
    async () => {
      setisLoading(true);
      return await axiosInstance.post("/report/project-suggestion", formData);
    },
    (result: any) => {
      if (IS_SUCCESS_RESPONSE(result)) {
        setisLoading(false);
        setData(result?.data?.data);
        // setreportUrl(result?.data?.data?.s3Url)
      }
    },
    () => {
      setisLoading(false);
    }
  );

  useEffect(() => {
    if (currentUser && formData && Object.entries(formData).length > 0) {
      handleProjectSuggestionData();
    }
  }, [formData, currentUser]);

  if (!currentUser) {
    return <div>
      <PhoneNumberVerification />``
    </div>
  }

  let projectParameter = ["Units Delivered to Customer", "Total Landed Price", "Total Financial Gain", "Investment"];
  let projectParameterWhitebg = [
    "Capacity Utilization Factor",
    "Total Loss",
    "Generation Tariff",
    "OA Charges",
    "Other Charges",
    "Estimated Savings",
    "GST Offset",
    "Textile Subsidy",
    "Balance Investment",
  ];

  let projectParameterOrange = [
    "Generation",
    "Loss Impact",
    "GST Offset",
    "Total Savings in Electricity Charges",
    "Investment",
    "Payback (Standalone)",
  ];

  return (
    <div className="w-full max-sm:overflow-auto ">
      <div className="flex justify-end mb-4">
        <Button size={'xs'} onClick={async () => {
          // window.open(reportUrl, '_blank', 'noopener,noreferrer')
          try {
            setdownloading(true)
            // Make POST request with axios
            const response: AxiosResponse = await axiosInstance.post("/report/project-suggestion/export", formData, {
              timeout: 5000,
              responseType: "blob"
            }).then((d: any) => d);
            console.log({ response });


            // Create blob from response
            const blob = new Blob([response.data], { type: 'application/pdf' });

            // Create download link
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', "TDR-report");
            document.body.appendChild(link);
            link.click();

            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

            // if (onSuccess) {
            //   onSuccess();
            // }
          } catch (err) {
            const error = err as Error | AxiosError;
            if (axios.isAxiosError(error)) {
              if (error) {
                error.response?.data?.message &&
                  toast({
                    variant: "destructive",
                    title: error.response?.data?.message || "Unknown Error",
                    description: "",
                  });
            
                // }
              }
            } 
            setdownloading(false)

          } finally {
            setdownloading(false)
          }
        }} variant={'outlinePrimary'} disabled={downloading} className="flex space-x-2">
          <span>{downloading ? <span> <Loader2Icon className="animate-spin" size={20} /></span> : <span> <DownloadCloud size={20} /></span>}</span>
          <span>Download Report</span>
        </Button>
      </div>
      <Table className="w-full ">
        <TableHeader className="bg-gray-800">
          <TableRow>
            {headerData.map((header, index) => (
              <TableHead
                key={index}
                className={`font-semibold text-white text-lg font-serif ${index === 0 ? "sticky left-0 " : "text-center"
                  }`}
              >
                {header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody className="w-full">
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={headerData.length}>
                <div className="py-4 text-center text-sm">Please Wait...</div>
              </TableCell>
            </TableRow>
          ) : Array.isArray(data) && data?.length > 0 ? (
            <>
              {data.map((row, index) => (
                <TableRow
                  key={index}
                  className={cn("bg-sky-200 hover:bg-blue-100", {
                    "bg-green-300 hover:bg-green-300 text-black font-bold": projectParameter.includes(row?.name),
                    "text-black font-bold": row.name === "Project Capacity",
                    "bg-gray-100 hover:bg-gray-100": projectParameterWhitebg.includes(row.name),
                  })}
                >
                  <TableCell className={cn(" sticky left-0", {})}>{row?.name}</TableCell>
                  <TableCell className="text-center">{row.unit}</TableCell>
                  <TableCell
                    className={cn("text-center", {
                      "bg-amber-100": projectParameterOrange.includes(row?.name),
                      "bg-[#FFFF00]": row.name === "Generation Tariff",
                    })}
                  >
                    {row?.unit === "Months" ? Math.round(row?.CPP) : row.CPP?.toFixed(2)}
                  </TableCell>
                  <TableCell
                    className={cn("text-center", {
                      "bg-amber-100": projectParameterOrange.includes(row?.name),
                      "bg-[#FFFF00]": row.name === "Generation Tariff",
                    })}
                  >
                    {row?.unit === "Months" ? Math.round(row?.GCP) : row.GCP?.toFixed(2)}
                  </TableCell>
                  <TableCell
                    className={cn("text-center", {
                      "bg-amber-100": projectParameterOrange.includes(row?.name),
                      "bg-[#FFFF00]": row.name === "Generation Tariff",
                    })}
                  >
                    {row?.unit === "Months" ? Math.round(row?.TPOA) : row.TPOA?.toFixed(2)}
                  </TableCell>
                  {/* <TableCell className="text-center">{row.cppAft}</TableCell> */}
                </TableRow>
              ))}
            </>
          ) : (
            <TableRow>
              <TableCell colSpan={headerData.length}>
                <div className="py-4 text-center text-sm">No Data</div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default Projectsuggestion;
